import axios from 'axios';
import { history } from '@umijs/max';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import {
  LOGIN_EXPIRED_CODES,
  LOGIN_PATH,
  NO_PERMISSION_CODE,
  REDIRECT_QUERY_KEY,
  REFRESH_REQUIRED_CODE,
  REQUEST_TIMEOUT,
  SESSION_EXPIRED_MESSAGE,
  SUCCESS_CODE,
} from '@/constants/auth';
import { refreshTokenRequest } from '@/services/modules/auth';
import { useAuthStore } from '@/stores/auth';
import {
  clearStoredTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
} from '@/utils/token';
import { antdApp } from '@/utils/antdApp';
import type { ApiResponse } from './types';

type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
};

export class ApiRequestError extends Error {
  code?: number;
  data?: unknown;
  notified?: boolean;

  constructor(
    messageText: string,
    options?: { code?: number; data?: unknown; notified?: boolean },
  ) {
    super(messageText);
    this.name = 'ApiRequestError';
    this.code = options?.code;
    this.data = options?.data;
    this.notified = options?.notified;
  }
}

let refreshPromise: Promise<string> | null = null;

const setAuthorizationHeader = (
  headers: AxiosRequestConfig['headers'],
  accessToken?: string,
): AxiosRequestHeaders => {
  const nextHeaders = {
    ...(headers as Record<string, unknown> | undefined),
  } as AxiosRequestHeaders;

  if (!accessToken) {
    return nextHeaders;
  }

  // 兼容旧项目后端，Authorization 直接放 token 原文。
  (nextHeaders as Record<string, string>).Authorization = accessToken;
  return nextHeaders;
};

const redirectToLogin = () => {
  if (history.location.pathname.startsWith(LOGIN_PATH)) {
    return;
  }

  const searchParams = new URLSearchParams();
  searchParams.set(
    REDIRECT_QUERY_KEY,
    `${history.location.pathname}${history.location.search}`,
  );

  history.replace({
    pathname: LOGIN_PATH,
    search: searchParams.toString(),
  });
};

const handleSessionExpired = () => {
  clearStoredTokens();
  useAuthStore.getState().clearSessionState();
  antdApp.message.error(SESSION_EXPIRED_MESSAGE);
  redirectToLogin();
};

const shouldSkipBusinessErrorNotify = (code?: number) =>
  code === REFRESH_REQUIRED_CODE ||
  (typeof code === 'number' && LOGIN_EXPIRED_CODES.includes(code));

const notifyBusinessError = (payload?: ApiResponse<unknown>) => {
  if (!payload || shouldSkipBusinessErrorNotify(payload.code)) {
    return false;
  }

  antdApp.message.error(
    payload.message ||
      (payload.code === NO_PERMISSION_CODE ? '无权限' : '请求失败'),
  );
  return true;
};

const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL || '',
  timeout: REQUEST_TIMEOUT,
});

httpClient.interceptors.request.use((config) => {
  const accessToken = getStoredAccessToken();

  if (accessToken) {
    config.headers = setAuthorizationHeader(config.headers, accessToken);
  }

  return config;
});

const refreshAccessToken = async () => {
  // 并发请求只允许发起一次刷新，其他请求全部等待同一个 promise。
  if (!refreshPromise) {
    const refreshToken = getStoredRefreshToken();
    const accessToken = getStoredAccessToken();

    if (!refreshToken && !accessToken) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }

    refreshPromise = refreshTokenRequest({
      refreshToken: refreshToken || undefined,
      accessToken: accessToken || undefined,
    })
      .then((tokenPayload) => {
        useAuthStore.getState().setTokens(tokenPayload);
        return tokenPayload.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

httpClient.interceptors.response.use(
  async (response) => {
    const payload = response.data as ApiResponse<unknown>;

    if (payload?.code === SUCCESS_CODE) {
      return response;
    }

    const requestConfig = response.config as RetryableRequestConfig;
    const needRefresh =
      payload?.code === REFRESH_REQUIRED_CODE ||
      LOGIN_EXPIRED_CODES.includes(payload?.code);

    // 业务码表示登录态失效时，自动刷新并重放原请求。
    if (needRefresh && !requestConfig._retry && !requestConfig.skipAuthRefresh) {
      try {
        requestConfig._retry = true;
        const newAccessToken = await refreshAccessToken();
        requestConfig.headers = setAuthorizationHeader(
          requestConfig.headers,
          newAccessToken,
        );
        return httpClient(requestConfig);
      } catch (_error) {
        handleSessionExpired();
      }
    }

    const notified = notifyBusinessError(payload);
    return Promise.reject(
      new ApiRequestError(payload?.message || '请求失败', {
        code: payload?.code,
        data: payload?.data,
        notified,
      }),
    );
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const requestConfig = error.config as RetryableRequestConfig | undefined;
    const isUnauthorized = error.response?.status === 401;

    // 一部分接口会直接返回 401，这里兜底走同样的刷新逻辑。
    if (isUnauthorized && requestConfig && !requestConfig._retry) {
      try {
        requestConfig._retry = true;
        const newAccessToken = await refreshAccessToken();
        requestConfig.headers = setAuthorizationHeader(
          requestConfig.headers,
          newAccessToken,
        );
        return httpClient(requestConfig);
      } catch (_refreshError) {
        handleSessionExpired();
      }
    }

    if (isUnauthorized) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    return Promise.reject(
      new Error(error.response?.data?.message || error.message || '网络异常'),
    );
  },
);

export const request = async <T>(config: AxiosRequestConfig) => {
  const response = await httpClient.request<ApiResponse<T>>(config);
  return response.data.data;
};

export default httpClient;
